import { apiFetch } from '../../lib/apiFetch.js';

// ⚠️ UPDATE THIS after running setup_api_gateway.sh
const API_BASE_URL = "https://b5bpqa285b.execute-api.ap-south-1.amazonaws.com";

// ⚠️ UPDATE THIS after running setup_ic_file_api_gateway.sh
const IC_FILE_API_BASE_URL = "https://s8ngo3lrv8.execute-api.ap-south-1.amazonaws.com";

// ⚠️ UPDATE THIS after running setup_listing_optimizer_api_gateway.sh
const OPTIMIZER_API_BASE_URL = "https://ccmvpzpld0.execute-api.ap-south-1.amazonaws.com";

// ============================================================================
// AMAZON LISTING API (existing)
// ============================================================================
export const listingApi = {
  // Start a listing creation job
  async runJob(mode, asins, userEmail) {
    const response = await apiFetch(`${API_BASE_URL}/api/run`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode, asins, user_email: userEmail }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Failed to start job: ${response.status}`);
    }
    return response.json();
  },

  // Get job status
  async getJobStatus(jobId) {
    const response = await apiFetch(`${API_BASE_URL}/api/job-status/${jobId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch job status: ${response.status}`);
    }
    return response.json();
  },

  // Get recent jobs
  async getRecentJobs() {
    const response = await apiFetch(`${API_BASE_URL}/api/recent-jobs`);
    if (!response.ok) {
      throw new Error(`Failed to fetch recent jobs: ${response.status}`);
    }
    return response.json();
  },

  // Download job output
  async downloadJobOutput(jobId) {
    const response = await apiFetch(`${API_BASE_URL}/api/download/${jobId}`);
    if (!response.ok) {
      throw new Error(`Failed to get download URL: ${response.status}`);
    }
    const data = await response.json();
    if (data.download_url) {
      window.open(data.download_url, "_blank");
    } else {
      throw new Error("No download URL available");
    }
  },

  // Get job logs
  async getJobLogs(jobId) {
    const response = await apiFetch(`${API_BASE_URL}/api/logs/${jobId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch logs: ${response.status}`);
    }
    return response.json();
  },
};

// ============================================================================
// IC FILE CONVERTER API (new — matches Image tools architecture)
// ============================================================================
export const icFileApi = {
  // Generate run ID matching Image tools format: {username}_{timestamp}
  createRunId(username) {
    const user = (username || "unknown").replace(/[^a-zA-Z0-9]/g, "_");
    const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
    return `${user}_${ts}`;
  },

  // Get presigned URL for CSV upload (same pattern as Link Generator)
  async getUploadUrl(filename, userEmail, platform) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename,
        user_email: userEmail,
        platform,
      }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Failed to get upload URL: ${response.status}`);
    }
    return response.json();
  },

  // Upload file directly to S3 via presigned URL (same as Link Generator)
  async uploadFileToS3(presignedUrl, file) {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": "text/csv" },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
    return response;
  },

  // Start conversion after file is uploaded
  async startConversion(runId) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/start/${runId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Failed to start conversion: ${response.status}`);
    }
    return response.json();
  },

  // Pre-flight: sample image URLs from the uploaded CSV and report counts
  // by format. Used to show a non-blocking warning when WebP is detected
  // before the seller commits to the run.
  async sniffImages(runId) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/sniff-images/${runId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      // Sniff is advisory — never block the run on a failure.
      return null;
    }
    return response.json();
  },

  // Get run status (metadata)
  async getStatus(runId) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/status/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch status: ${response.status}`);
    }
    return response.json();
  },

  // Download output via Lambda proxy (matches Image tools — blob download, NOT presigned URL)
  async downloadOutput(runId) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/download/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to download: ${response.status}`);
    }

    // Extract filename from Content-Disposition header, fallback to run_id
    let filename = `smartbiz_${runId}.xlsx`;
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }

    // Lambda returns binary file — create blob and trigger download
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Get processing logs
  async getLogs(runId) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/logs/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch logs: ${response.status}`);
    }
    return response.json();
  },

  // Get recent IC File conversion runs
  async getRecentRuns() {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/recent-runs`);
    if (!response.ok) {
      throw new Error(`Failed to fetch recent runs: ${response.status}`);
    }
    return response.json();
  },

  // Submit feedback (stored at s3://smartbiz-lead-enrichment/feedback/)
  async submitFeedback({ rating, tool, category, comment, user, page }) {
    const response = await apiFetch(`${IC_FILE_API_BASE_URL}/api/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, tool, category, comment, user, page }),
    });
    if (!response.ok) {
      throw new Error(`Failed to submit feedback: ${response.status}`);
    }
    return response.json();
  },
};


// ============================================================================
// LISTING OPTIMIZER API (D2C optimization — same architecture as IC File)
// ============================================================================
export const optimizerApi = {
  // Get presigned URL for file upload
  async getUploadUrl(filename, userEmail) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename, user_email: userEmail }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Failed to get upload URL: ${response.status}`);
    }
    return response.json();
  },

  // Upload file directly to S3 via presigned URL
  async uploadFileToS3(presignedUrl, file, contentType) {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
    return response;
  },

  // Detect product category from uploaded file
  async detectCategory(runId) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/detect-category/${runId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Category detection failed: ${response.status}`);
    }
    return response.json();
  },

  // Generate 3 style previews for the first product
  async generatePreviews(runId, category) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/preview/${runId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Preview generation failed: ${response.status}`);
    }
    return response.json();
  },

  // Start async batch processing with selected vibe
  async startProcessing(runId, vibeIndex) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/start/${runId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ vibe_index: vibeIndex }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || `Failed to start processing: ${response.status}`);
    }
    return response.json();
  },

  // Get job status (metadata)
  async getStatus(runId) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/status/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch status: ${response.status}`);
    }
    return response.json();
  },

  // Get processing logs
  async getLogs(runId) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/logs/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch logs: ${response.status}`);
    }
    return response.json();
  },

  // Download output via Lambda proxy (same blob pattern as icFileApi.downloadOutput)
  async downloadOutput(runId) {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/download/${runId}`);
    if (!response.ok) {
      throw new Error(`Failed to download: ${response.status}`);
    }

    // Extract filename from Content-Disposition header
    let filename = `optimized_${runId}.xlsx`;
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }

    // Lambda returns binary file — create blob and trigger download
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Get recent optimizer runs
  async getRecentRuns() {
    const response = await apiFetch(`${OPTIMIZER_API_BASE_URL}/api/optimizer/recent-runs`);
    if (!response.ok) {
      throw new Error(`Failed to fetch recent runs: ${response.status}`);
    }
    return response.json();
  },
};
